// REACT IMPORTS
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// COMPONENT IMPORTS
import { useAppDispatch, useAppSelector } from "store/hooks";
import { companyMarkersGet } from "store/slices/paymentsSlice";
import { RootState } from "store/store";
import {
  emailIdsGet,
  phoneNumbersGet,
  sendVerification,
  useAccountDetails,
  useEmailOptions,
  usePhoneNumberOptions,
  useUserData,
} from "store/slices/authSlice";
import Button from "components/form/Button";
import InputSelect from "components/form/InputSelect";
import SupportPhoneLink from "components/auth/SupportPhoneLink";

// TYPES IMPORT
import { otpDetailsTypes } from "types/authTypes";
import InputRadio from "components/form/InputRadio";
import {
  clearSlice,
  onGetAccountPermission,
  onGetPortalSettings,
} from "utils/HelperFunctions";
import { TOKEN_STORAGE_KEY } from "store/api/axiosApi";
import { decode } from "utils/HelperFunctions";
import { useTid } from "utils/testid";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import { activateOnKey } from "utils/activateOnKey";
import { useServerError } from "hooks/useServerError";
import ServerErrorAlert from "./ServerErrorAlert";
import { LOGIN_ERROR_MESSAGES } from "utils/apiError";

/* ------------------------------------- SENT OTP FORM ------------------------------------- */

// Set when the form is shown inside the guest login overlay of a link page:
// there is no cluster in the address and nowhere to navigate to, so the
// overlay supplies the cluster and is told when each step is done.
export interface GuestSendOtp {
  clusterId: string;
  onSent: (destination: any) => void;
  onCancel: () => void;
}

const SendOtpForm = ({ guest }: { guest?: GuestSendOtp }) => {
  const tid = useTid();
  const dispatch = useAppDispatch();
  const accountDetails = useAccountDetails();
  const userData = useUserData();
  const phoneNumberOptions = usePhoneNumberOptions();
  const emailOptions = useEmailOptions();
  const { companyMarkers } = useAppSelector(
    (state: RootState) => state.payment
  );

  const navigate = useNavigate();

  const initialVal = {
    isEmail: false,
    email_id: "",
    isPhone: false,
    phone_number_id: "",
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { serverError, showError, clearError } = useServerError();
  const [option, setOption] = useState<"email" | "phoneNumber">("email");
  const [otpDetails, setOtpDetails] = useState<otpDetailsTypes>(initialVal);
  // True once the account's contacts are known; a failed lookup says nothing
  // about what is on file, so it never hides an option.
  const [contactsLoaded, setContactsLoaded] = useState<boolean>(false);
  const { clusterId: cluster_id } = useParams();
  const clusterId = guest ? guest.clusterId : decode(cluster_id || "", true);
  const handleLogOut = () => {
    if (guest) {
      guest.onCancel();
      return;
    }
    clearSlice(dispatch);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.clear();
    navigate(`/${cluster_id}/login`, { replace: true });
  };

  const onGetEmailIds = async () => {
    try {
      let payload = { accounts_id: userData?.data?.accountData?.id };
      const res: any = await dispatch(emailIdsGet(payload)).unwrap();
      return Boolean(res?.body?.success);
    } catch (error: any) {
      console.log("get email ids err: --->", error?.message);
      showError(error, LOGIN_ERROR_MESSAGES.loadContacts);
      return false;
    }
  };

  const onGetPhoneNumbers = async () => {
    try {
      let payload = { accounts_id: userData?.data?.accountData?.id };
      const res: any = await dispatch(phoneNumbersGet(payload)).unwrap();
      return Boolean(res?.body?.success);
    } catch (error: any) {
      console.log("get email ids err: --->", error?.message);
      showError(error, LOGIN_ERROR_MESSAGES.loadContacts);
      return false;
    }
  };

  const onSubmit = async () => {
    let number;
    let email;
    setIsLoading(true);
    clearError();
    try {
      let payload: any = {
        ...otpDetails,
        accounts_id: userData?.data?.accountData?.id,
      };
      if (!payload?.isEmail) {
        delete payload["email_id"];
        delete payload["isEmail"];
        number = phoneNumberOptions.filter(
          (val: any) => val.value === payload?.phone_number_id
        );
      }
      if (!payload?.isPhone) {
        delete payload["phone_number_id"];
        delete payload["isPhone"];
        email = emailOptions.filter(
          (val: any) => val.value === payload?.email_id
        );
      }
      let res: any = await dispatch(sendVerification(payload)).unwrap();
      if (res?.body?.success) {
        await onGetPermssions();
        await onGetSettings();
        const destination = {
          number: number?.[0] || undefined,
          email: email?.[0] || undefined,
        };
        if (guest) {
          guest.onSent(destination);
        } else {
          // Keep the masked destination across refreshes of the OTP screen
          sessionStorage.setItem(
            "otp-destination",
            JSON.stringify(destination)
          );
          navigate(`/${cluster_id}/otp-verification`, {
            state: destination,
          });
        }
      } else {
        // Sending has no inline error, so every refusal (daily limit, no SMS
        // set up, the email or SMS provider failing) is told here.
        showError(res, LOGIN_ERROR_MESSAGES.sendOtp);
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.log("send verification code error: -->", error?.message);
      showError(error, LOGIN_ERROR_MESSAGES.sendOtp);
    }
  };

  const onGetPermssions = async () => {
    try {
      let payload = {
        cluster_id: clusterId,
      };
      await onGetAccountPermission(payload, dispatch);
    } catch (error) {
      console.log("onGetPermssions error =====", error);
    }
  };
  const onGetSettings = async () => {
    try {
      let payload = {
        cluster_id: clusterId,
      };
      await onGetPortalSettings(payload, dispatch);
    } catch (error) {
      console.log("error =====", error);
    }
  };

  // A new choice of contact or method clears the last error.
  useEffect(() => {
    clearError();
  }, [otpDetails, option, clearError]);

  useEffect(() => {
    let isCurrent = true;
    const loadContacts = async () => {
      setContactsLoaded(false);
      let lookups;
      if (accountDetails?.data?.two_auth_field !== "both") {
        if (accountDetails?.data?.two_auth_field === "phone") {
          lookups = [onGetPhoneNumbers()];
        } else {
          lookups = [onGetEmailIds()];
        }
      } else {
        lookups = [onGetPhoneNumbers(), onGetEmailIds()];
      }
      const results = await Promise.all(lookups);
      if (isCurrent) {
        setContactsLoaded(results.every(Boolean));
      }
    };
    loadContacts();
    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountDetails]);

  useEffect(() => {
    // The no-contact message points to the branding Support Phone; fetch the
    // markers on direct page loads
    if (!companyMarkers?.["Support Phone"] && clusterId) {
      dispatch(companyMarkersGet({ cluster_id: clusterId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterId]);

  const twoAuthField = accountDetails?.data?.two_auth_field;
  const hasEmail = emailOptions?.length > 0;
  const hasPhone = phoneNumberOptions?.length > 0;

  // The one way a code can be sent, or null when the customer gets to choose.
  // An account with nothing on file for a method is never offered that method.
  const singleMethod = (): "email" | "phoneNumber" | null => {
    if (twoAuthField !== "both") {
      return twoAuthField === "phone" ? "phoneNumber" : "email";
    }
    if (contactsLoaded && hasEmail !== hasPhone) {
      return hasEmail ? "email" : "phoneNumber";
    }
    return null;
  };
  const fixedMethod = singleMethod();
  const activeMethod = fixedMethod || option;

  useEffect(() => {
    // One phone or email leaves nothing to choose, so it starts selected
    const options =
      activeMethod === "phoneNumber" ? phoneNumberOptions : emailOptions;
    if (!contactsLoaded || options?.length !== 1) {
      return;
    }
    setOtpDetails(
      activeMethod === "phoneNumber"
        ? { ...initialVal, phone_number_id: options[0].value, isPhone: true }
        : { ...initialVal, email_id: options[0].value, isEmail: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsLoaded, activeMethod, emailOptions, phoneNumberOptions]);

  const noContactOnFile =
    contactsLoaded &&
    (twoAuthField === "both"
      ? !hasEmail && !hasPhone
      : activeMethod === "phoneNumber"
        ? !hasPhone
        : !hasEmail);
  const missingContact =
    twoAuthField === "both"
      ? "an email address or phone number"
      : activeMethod === "phoneNumber"
        ? "a phone number"
        : "an email address";

  return (
    <div
      className="w-full lg:max-w-[520px] animate__animated animate__fadeIn"
      data-testid={tid("form-shell")}
    >
      <div className="lg:text-3xl text-xl font-bold -tracking-[0.6px] leading-normal text-txtPrimary">
        {noContactOnFile
          ? "Unable to Send Your OTP"
          : fixedMethod
            ? fixedMethod === "phoneNumber"
              ? "Choose Your Phone Number for OTP"
              : "Choose Your Email for OTP"
            : "OTP Delivery Choice"}
      </div>
      <div className="lg:py-10 pt-6 pb-10">
        {!noContactOnFile && (
          <p className="lg:text-base text-sm leading-normal font-normal text-txtParagraph">
            {fixedMethod
              ? fixedMethod === "phoneNumber"
                ? "Please select the phone number where you would like to receive your one-time password (OTP)."
                : "Please select the email address where you would like to receive your one-time password (OTP)."
              : "Select how you wish to receive your one-time password (OTP). You can choose between your registered email addresses or phone numbers."}
          </p>
        )}
        {!fixedMethod && !noContactOnFile ? (
          <div className="pt-5">
            <InputRadio
              name="otp-delivery-method"
              data-testid={tid("delivery-method-radio-group")}
              aria-label="OTP delivery method"
              options={[
                { name: "Email", value: "email" },
                { name: "Phone number", value: "phoneNumber" },
              ]}
              value={option}
              onChange={(e: any) => {
                setOption(e?.target?.value);
                setOtpDetails(initialVal);
              }}
              className="!font-medium !text-sm uppercase"
            />
          </div>
        ) : null}
      </div>
      <div>
        {noContactOnFile ? (
          <div
            className="flex items-center gap-[5px] px-[10px] py-3 bg-lightRedBg rounded animate__animated animate__fadeIn"
            role="alert"
            data-testid={tid("no-contact-alert")}
          >
            <div className="shrink-0">
              <InfoRedCircleIcon aria-hidden="true" />
            </div>
            <div className="lg:text-base text-sm text-red leading-normal">
              We don't have {missingContact} on file for your account, so we
              can't send you a one-time password. Please contact our
              representative
              {companyMarkers?.["Support Phone"] ? (
                <>
                  {" "}
                  at{" "}
                  <SupportPhoneLink
                    phone={companyMarkers["Support Phone"]}
                    className="underline"
                  />
                </>
              ) : null}{" "}
              for help.
            </div>
          </div>
        ) : activeMethod === "phoneNumber" ? (
          <InputSelect
            label="Phone Number"
            placeholder="Select phone number"
            options={phoneNumberOptions || []}
            selected={otpDetails?.phone_number_id}
            data-testid={tid("phone-number-select")}
            aria-label="Phone number for OTP"
            setSelected={(e: any) =>
              setOtpDetails({
                ...otpDetails,
                phone_number_id: e?.value,
                isPhone: e && e?.value ? true : false,
              })
            }
            _className="!font-medium !text-base"
          />
        ) : (
          <InputSelect
            label="Email Address"
            placeholder="Select email address"
            options={emailOptions || []}
            selected={otpDetails?.email_id}
            data-testid={tid("email-address-select")}
            aria-label="Email address for OTP"
            setSelected={(e: any) =>
              setOtpDetails({
                ...otpDetails,
                email_id: e?.value,
                isEmail: e && e?.value ? true : false,
              })
            }
            _className="!font-medium !text-base"
          />
        )}
      </div>
      <div className="lg:max-w-[520px] w-full pt-10 lg:pt-[60px]">
        {!noContactOnFile && (
          <Button
            isLoading={isLoading}
            className="w-full"
            text="Send OTP"
            data-testid={tid("send-otp-button")}
            aria-label="Send one-time password"
            isDisabled={!otpDetails?.email_id && !otpDetails?.phone_number_id}
            onClick={() => onSubmit()}
            variant="variable"
          />
        )}
        <ServerErrorAlert
          message={serverError}
          className="mt-3"
          data-testid={tid("server-error-alert")}
        />
        <div className="pt-3 w-full grid place-items-center">
          <div
            className="lg:py-[10px] py-[2px] px-4 text-blue hover:!text-btnPrimaryHover"
            onClick={handleLogOut}
            onKeyDown={activateOnKey(handleLogOut)}
            role="button"
            tabIndex={0}
            data-testid={tid("go-back-button")}
            aria-label="Go back to login"
          >
            Go back
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendOtpForm;
